import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { SortingState } from '@tanstack/react-table';
import { useHistory, useLocation } from 'react-router-dom';

import type { ReportDateRangeValue } from './api/fetchReportData';
import {
    DEFAULT_REPORT_PAGE,
    parseReportQueryParams,
    serializeReportQueryParams,
    sortingStateToQuerySort,
    type ReportPageLimit,
    type ReportQueryParams,
} from './reportQueryParams';

const SEARCH_DEBOUNCE_MS = 300;

function parseRememberedReportParams(search: string, storageKey: string): ReportQueryParams {
    const urlParams = new URLSearchParams(search);
    // Shared links and explicit selections always take precedence over local preferences.
    if (!urlParams.has('report') && !urlParams.has('view')) {
        try {
            const stored = JSON.parse(localStorage.getItem(storageKey) ?? 'null');
            if (typeof stored?.report === 'string' && stored.report) {
                urlParams.set('report', stored.report);
                if (Number.isInteger(stored.view) && stored.view > 0) {
                    urlParams.set('view', String(stored.view));
                }
            }
        } catch {
            // Storage may be unavailable or contain an old/invalid preference.
        }
    }
    return parseReportQueryParams(urlParams.toString());
}

export function useReportQueryParams(userId?: string | number) {
    const history = useHistory();
    const location = useLocation();

    const storageKey =
        'frontdoor_last_report:' + JSON.stringify([userId ?? null, location.pathname]);
    const parseParams = useCallback(
        (search: string) => parseRememberedReportParams(search, storageKey),
        [storageKey],
    );
    const params = useMemo(() => parseParams(location.search), [location.search, parseParams]);

    const [searchInput, setSearchInput] = useState(params.q);
    const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        setSearchInput(params.q);
    }, [params.q]);

    useEffect(() => {
        return () => {
            if (searchDebounceRef.current) {
                clearTimeout(searchDebounceRef.current);
            }
        };
    }, []);

    const replaceParams = useCallback(
        (patch: Partial<ReportQueryParams>) => {
            const current = parseParams(history.location.search);
            const next = serializeReportQueryParams({ ...current, ...patch });
            const search = next.toString();

            history.replace({
                pathname: location.pathname,
                search: search ? `?${search}` : '',
            });
        },
        [history, location.pathname, parseParams],
    );

    useEffect(() => {
        try {
            localStorage.setItem(
                storageKey,
                JSON.stringify({ report: params.report, view: params.view }),
            );
        } catch {
            // Report navigation must still work when browser storage is disabled.
        }

        const urlParams = new URLSearchParams(location.search);
        if (!urlParams.has('report') && !urlParams.has('view')) {
            // Materialize the restored selection so subsequent edits and copied URLs retain it.
            replaceParams({ report: params.report, view: params.view });
        }
    }, [storageKey, params.report, params.view, location.search, replaceParams]);

    const setReport = useCallback(
        (report: string) => {
            replaceParams({
                report,
                view: undefined,
                page: DEFAULT_REPORT_PAGE,
                tickets: undefined,
            });
        },
        [replaceParams],
    );

    const setView = useCallback(
        (view: number | undefined, report?: string) => {
            replaceParams({
                ...(report ? { report } : {}),
                view,
                page: DEFAULT_REPORT_PAGE,
            });
        },
        [replaceParams],
    );

    const setRange = useCallback(
        (value: ReportDateRangeValue) => {
            replaceParams({
                range: value.range,
                customStart: value.customStart,
                customEnd: value.customEnd,
                page: DEFAULT_REPORT_PAGE,
            });
        },
        [replaceParams],
    );

    const setSearch = useCallback(
        (q: string) => {
            setSearchInput(q);

            if (searchDebounceRef.current) {
                clearTimeout(searchDebounceRef.current);
            }

            searchDebounceRef.current = setTimeout(() => {
                const current = parseParams(history.location.search);
                if (current.q === q) {
                    return;
                }

                replaceParams({ q, page: DEFAULT_REPORT_PAGE });
            }, SEARCH_DEBOUNCE_MS);
        },
        [history, replaceParams, parseParams],
    );

    const setSort = useCallback(
        (sort: SortingState) => {
            const current = parseParams(history.location.search);
            replaceParams({ sort: sortingStateToQuerySort(sort, current.report) });
        },
        [history, replaceParams, parseParams],
    );

    const setPage = useCallback(
        (page: number) => {
            if (searchDebounceRef.current) {
                clearTimeout(searchDebounceRef.current);
                searchDebounceRef.current = null;
            }

            replaceParams({ page });
        },
        [replaceParams],
    );

    const setLimit = useCallback(
        (limit: ReportPageLimit) => {
            replaceParams({ limit, page: DEFAULT_REPORT_PAGE });
        },
        [replaceParams],
    );

    const setTickets = useCallback(
        (tickets: number[] | undefined) => {
            replaceParams({ tickets, page: DEFAULT_REPORT_PAGE });
        },
        [replaceParams],
    );

    return {
        params,
        searchInput,
        setReport,
        setView,
        setRange,
        setSearch,
        setSort,
        setPage,
        setLimit,
        setTickets,
        replaceParams,
    };
}
